/**
 * Sync form state from rows when selection changes.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useEffect, useRef } from 'react';
import type { Gateway, LeadsList, ShortLinkTld } from '@/hooks';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import { MAX_SELECTED_TEXTS } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';

export function useScaleDropFormSyncFromRows(
  rows: DropsWithStatsRow[],
  isMulti: boolean,
  routeMatch: Gateway | null,
  leadsMatch: LeadsList | null,
  tldMatch: ShortLinkTld | null,
  mergedSendersFromRows: string[],
  mergedTextsFromRows: string[],
  _smsTexts: string[],
  allDropsHaveGateway: boolean,
  defaultGatewayForMulti: number,
  defaultStaticTagIdFromRows: string | null,
  setters: {
    setAmount: (v: number | '') => void;
    setRouteId: (v: number) => void;
    setLeadsListWiserId: (v: number) => void;
    setTldValue: (v: string) => void;
    setSelectedSenderNames: (v: string[]) => void;
    setSelectedSmsTexts: (v: string[]) => void;
    setSelectedStaticTagId?: (v: string | null) => void;
  },
  hasUserEditedRef: React.MutableRefObject<boolean>,
) {
  const initKey = rows[0]?.drop?.uniqueId ?? rows[0]?.drop?.id ?? null;
  const prevInitKeyRef = useRef<string | number | null>(null);

  useEffect(() => {
    if (rows.length === 0 || initKey == null) return;
    const initKeyChanged = prevInitKeyRef.current !== initKey;
    if (initKeyChanged) {
      prevInitKeyRef.current = initKey;
      hasUserEditedRef.current = false;
      if (setters.setSelectedStaticTagId) setters.setSelectedStaticTagId(defaultStaticTagIdFromRows);
    }
    if (hasUserEditedRef.current) return;

    const first = rows[0]!.drop;
    setters.setAmount(first?.cap ?? 500);
    if (!isMulti) {
      if (routeMatch) setters.setRouteId(routeMatch.id);
      if (leadsMatch) setters.setLeadsListWiserId(leadsMatch.id);
      if (tldMatch) setters.setTldValue(tldMatch.value);
      setters.setSelectedSenderNames(mergedSendersFromRows);
      // All of the drop's texts are in textsList (mergedTextsFromRows is now merged in),
      // so no filtering against the global creative list is needed.
      setters.setSelectedSmsTexts(mergedTextsFromRows.slice(0, MAX_SELECTED_TEXTS));
    } else if (!allDropsHaveGateway) setters.setRouteId(defaultGatewayForMulti);
  }, [
    initKey,
    isMulti,
    routeMatch,
    leadsMatch,
    tldMatch,
    mergedSendersFromRows,
    mergedTextsFromRows,
    allDropsHaveGateway,
    defaultGatewayForMulti,
    defaultStaticTagIdFromRows,
    rows,
    setters,
  ]);
}
